// hooks/useOverlaySigns.ts
import { useCallback, useEffect, useState } from 'react';
import { ImageMarker, videoProjectService } from 'services/videoService';

interface OverlaySign extends ImageMarker {
  projectId: number;
  projectName: string;
  distance: number;
  isOverlay: true;
}

interface UseOverlaySignsProps {
  currentProjectId: number;
  selectedProjectIds: number[];
  enabled: boolean;
}

interface UseOverlaySignsReturn {
  overlaySigns: OverlaySign[];
  loading: boolean;
  error: string | null;
  projectNames: Map<number, string>;
  refresh: () => Promise<void>;
}

export function useOverlaySigns({
  currentProjectId,
  selectedProjectIds,
  enabled
}: UseOverlaySignsProps): UseOverlaySignsReturn {
  const [overlaySigns, setOverlaySigns] = useState<OverlaySign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectNames, setProjectNames] = useState<Map<number, string>>(
    new Map()
  );

  console.log('[useOverlaySigns] 🔍 Hook executado:', {
    enabled,
    selectedProjectIds,
    currentProjectId
  });

  const loadOverlaySigns = useCallback(async () => {
    if (!enabled || selectedProjectIds.length === 0) {
      setOverlaySigns([]);
      return;
    }

    console.log('[OverlaySigns] 🔄 Carregando sinalizações overlay...', {
      selectedProjects: selectedProjectIds
    });

    setLoading(true);
    setError(null);

    try {
      // Buscar todos os projetos para pegar os nomes
      const allProjects = await videoProjectService.getAll();
      const namesMap = new Map<number, string>();
      allProjects.forEach(p => namesMap.set(p.id, p.name));
      setProjectNames(namesMap);

      // ✅ PRÉ-CARREGAR TUDO UMA VEZ PARA TODOS OS PROJETOS
      console.log('[OverlaySigns] 📦 Pré-carregando dados...');
      const [devices, supports, services, films] = await Promise.all([
        videoProjectService.getSignalDevices(),
        videoProjectService.getAllSignalSupports(),
        videoProjectService.getSignalServices(), // ✅ NOVO
        videoProjectService.getSignalFilms() // ✅ NOVO
      ]);
      console.log('[OverlaySigns] ✅ Pré-carregamento completo:', {
        devices: devices.length,
        supports: supports.length,
        services: services.length,
        films: films.length
      });

      // ✅ Carregar sinalizações de todos os projetos selecionados
      const allMarkersArrays: OverlaySign[][] = [];

      for (const projectId of selectedProjectIds) {
        try {
          const markers = await videoProjectService.getProjectMarkers(
            projectId
          );
          console.log(
            `[OverlaySigns] ✅ Projeto ${projectId}: ${markers.length} sinalizações`
          );

          // ✅ Enriquecer markers com TODAS as relações do cache
          const enrichedMarkers: OverlaySign[] = markers.map(marker => {
            // Buscar device
            const device = marker.signal_device?.symbol_name
              ? marker.signal_device
              : devices.find(d => d.id === marker.signal_device_id);

            // Buscar support
            const support = marker.signal_support?.code
              ? marker.signal_support
              : supports.find(s => s.id === marker.signal_support_id);

            // ✅ Buscar service
            const service = marker.signal_service?.name
              ? marker.signal_service
              : services.find(s => s.id === marker.service_id);

            // ✅ Buscar film
            const film = marker.signal_film?.name
              ? marker.signal_film
              : films.find(f => f.id === marker.signal_film_id);

            if (!device) {
              console.warn(
                `[OverlaySigns] ⚠️ Marker ${marker.id} sem device completo`
              );
            }

            return {
              ...marker,
              signal_device: device,
              signal_support: support,
              signal_service: service, // ✅ NOVO
              signal_film: film, // ✅ NOVO
              projectId,
              projectName: namesMap.get(projectId) || `Projeto ${projectId}`,
              distance: 0,
              isOverlay: true as const
            };
          });

          allMarkersArrays.push(enrichedMarkers);
        } catch (err) {
          console.error(`[OverlaySigns] ❌ Erro projeto ${projectId}:`, err);
        }
      }

      const finalMarkers = allMarkersArrays.flat();

      console.log(
        `[OverlaySigns] 📊 Total de sinalizações overlay: ${finalMarkers.length}`
      );
      console.log(
        '[OverlaySigns] 📊 Amostra do primeiro marker:',
        finalMarkers[0]
          ? {
              id: finalMarkers[0].id,
              service: finalMarkers[0].signal_service?.name,
              film: finalMarkers[0].signal_film?.name,
              device: finalMarkers[0].signal_device?.code
            }
          : 'nenhum'
      );

      setOverlaySigns(finalMarkers);
    } catch (err) {
      console.error('[OverlaySigns] ❌ Erro geral:', err);
      setError('Erro ao carregar sinalizações de outros projetos');
    } finally {
      setLoading(false);
    }
  }, [enabled, selectedProjectIds, currentProjectId]);

  // Carregar quando os parâmetros mudarem
  useEffect(() => {
    loadOverlaySigns();
  }, [loadOverlaySigns]);

  const refresh = useCallback(async () => {
    await loadOverlaySigns();
  }, [loadOverlaySigns]);

  return {
    overlaySigns,
    loading,
    error,
    projectNames,
    refresh
  };
}
